import { IIncidentReportRead } from 'common/services/incident-report/incident-report.types';
import TableContextProvider, { useTableControls } from '../../table/table-context';
import { Button, Col, Row, Space } from 'antd';
import incidentReportService from 'common/services/incident-report/incident-report.service';
import { incidentReportColumns, incidentReportListFilter } from './incident-report-list.config';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { SearchFilter } from '../../table/filters/search-filter/search-filter';
import { CloudDownloadOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { observer } from 'mobx-react-lite';
import TableBase from 'common/components/table/table-base';
import { ColumnFilter } from '../../table/filters/column-filter/column-filter';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import React, { useEffect, useRef, useState } from 'react';
import { IIncidentReportListProps } from './incident-report-list.types';
import { IConfirmDeleteModalProps } from 'common/components/modal/confirm-delete-modal/confirm-delete-modal.types';
import ConfirmDeleteModal from 'common/components/modal/confirm-delete-modal/confirm-delete-modal';
import SendEmailModal from 'common/components/modal/send-email-modal/send-email-modal';
import PrintIncidentReport from 'common/pages/incident-report/print-incident-reports/print-incident-reports';
import { usePDF } from '@react-pdf/renderer';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import { saveAs } from 'file-saver';
import { IPrintIncidentReportData } from 'common/pages/incident-report/print-incident-reports/print-incident-reports.types';
import { trackPromise } from 'react-promise-tracker';
import { notification } from 'common/utils/notification';
import configurationStore from 'common/stores/configuration/configuration.store';
import featureStore from 'common/stores/features/feature.store';
import { getFile } from 'common/helpers/image.helper';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';

const { caseNumber, incidentDate, reportingPersion, category, community, location, description } = incidentReportColumns();
export const columns = [caseNumber, incidentDate, reportingPersion, category, community, location, description];

const MAX_EMAIL_FILE_SIZE_MB = 20;
const MAX_EMAIL_FILE_SIZE = MAX_EMAIL_FILE_SIZE_MB * 1024 * 1024;

export const IncidentReportList = observer(({ showCreateButton, showDeleteButton, baseRoute }: IIncidentReportListProps) => {
  const navigate = useNavigate();
  const [currentSelectedRow, setCurrentSelectedRow] = useState<IIncidentReportRead | undefined>(undefined);

  const tableControls = useTableControls({
    onSelectedRow: (selectedRow: IIncidentReportRead) => {
      setCurrentSelectedRow(selectedRow);
    },
    onFetch: incidentReportService.getIncidents,
    initialColumns: columns,
  });

  const handleEditClick = () => {
    if (!currentSelectedRow?.id) {
      return;
    }

    navigate(`${baseRoute}/incident-reports/${currentSelectedRow.id}`);
  };

  const deleteReportModal = useModal({
    onBeforeShow: (setModalInitData) => {
      if (!currentSelectedRow?.id) {
        return;
      }

      const data: IConfirmDeleteModalProps = {
        message: 'Are you sure you want to delete this incident report?',
        onDelete: () => {
          incidentReportService.deleteIncident(currentSelectedRow.id).then(() => {
            deleteReportModal.hideModal(true);
          });
        },
      };

      setModalInitData(data);
    },
    onClose: (isSuccess: boolean | undefined) => {
      if (isSuccess) {
        tableControls.setSelectedRow(null);
        tableControls.refetchData();
      }
    },
  });

  const iframeRef = useRef<any>(null);
  const isPdfReady = useRef<boolean>(false);
  const [pdfInstance, updatePdfInstance] = usePDF();
  const pdfInstanceRef = useRef(pdfInstance);

  const buildPrintProps = async (rows: IIncidentReportRead[]) => {
    return await Promise.all(
      rows.map(async (row) => {
        const attachments = row.attachments || [];
        const attachmentsSrc = await Promise.all(attachments.map(async (a) => await getFile(a.fileUri, UploadFileTypes.IncidentReport)));

        return {
          ...row,
          attachmentsSrc,
        } as IPrintIncidentReportData;
      }),
    );
  };

  useEffect(() => {
    if (!pdfInstance.loading && pdfInstance.url && pdfInstance.blob) {
      isPdfReady.current = true;
    } else {
      isPdfReady.current = false;
    }

    pdfInstanceRef.current = pdfInstance;
  }, [pdfInstance]);

  const waitForPdfReady = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const timer = setInterval(() => {
        if (isPdfReady.current) {
          clearInterval(timer);
          resolve();
        }

        if (Date.now() - start > 20000) {
          clearInterval(timer);
          reject(new Error('PDF generation timeout'));
        }
      }, 200);
    });
  };

  const generatePdfForRow = async (rows: IIncidentReportRead[]): Promise<void> => {
    const result = await buildPrintProps(rows);
    const logo = await configurationStore.getCommunityLogoOrDefaultForPDF();
    updatePdfInstance(<PrintIncidentReport reports={result.reverse()} logo={logo} />);
    await waitForPdfReady();
  };

  const getDataToPrint = async () => {
    if (!tableControls.isAllRowsSelected) {
      return tableControls.getSelectedRows();
    }

    const result = await tableControls.getAllData();

    return result;
  };

  const handlePrint = async () => {
    const selectedRows = await getDataToPrint();
    if (!selectedRows.length) {
      return;
    }
    if (selectedRows.length > 10) {
      notification.warning({ message: 'You have selected more than 10 reports, please wait for the file to be ready.', duration: 10 });
    }
    await trackPromise(generatePdfForRow(selectedRows));
    const iframe = iframeRef.current;
    if (!iframe || !pdfInstanceRef.current.url) {
      return;
    }
    iframe.src = pdfInstanceRef.current.url;
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };
  };

  const getExportFileName = (rows: IIncidentReportRead[]) => {
    return rows.length === 1 ? `${rows[0].caseNumber}.pdf` : 'incident-reports.pdf';
  };

  const handleDownload = async () => {
    const selectedRows = await getDataToPrint();
    if (!selectedRows.length) {
      return;
    }
    await trackPromise(generatePdfForRow(selectedRows));
    if (pdfInstanceRef.current.blob) {
      saveAs(pdfInstanceRef.current.blob, getExportFileName(selectedRows));
    }
  };

  const sendReportsByEmailModal = useModal({
    onClose: async (isSuccess?: boolean, email?: string | null) => {
      if (!isSuccess || !email) {
        return;
      }
      const selectedRows = await getDataToPrint();
      if (!selectedRows.length) {
        return;
      }
      await trackPromise(generatePdfForRow(selectedRows));
      const pdfBlob = pdfInstanceRef.current.blob;
      if (!pdfBlob) {
        return;
      }
      if (pdfBlob.size > MAX_EMAIL_FILE_SIZE) {
        notification.warning({
          message: `The selected reports exceed ${MAX_EMAIL_FILE_SIZE_MB} MB. Please select fewer reports and try again.`,
          duration: 10,
        });
        return;
      }
      await incidentReportService.sendReportsByEmail(pdfBlob, getExportFileName(selectedRows), email);
      notification.success({
        message: `The download link for the exported PDF file has been sent to ${email}.`,
        duration: 10,
      });
    },
  });

  return (
    <>
      <iframe ref={iframeRef} style={{ width: 0, height: 0, border: 'none' }} title="pdf-print-frame" />
      <TableContextProvider controls={tableControls}>
        <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
          <Col flex="auto">
            <SearchFilter
              adaptiveSearch={true}
              enableProgressiveSearch={true}
              placeholder="Search by name, category, license plate, address, description"
              rulesForColumn={{
                category: FilterTypesEnum.StartWith,
                subCategory: FilterTypesEnum.StartWith,
                description: FilterTypesEnum.Contains, // TODO Discuss about removing this since it might be a performance issue
                address: FilterTypesEnum.StartWith,
                involvedVehicle: FilterTypesEnum.StartWith,
                involvedPerson: FilterTypesEnum.StartWith,
              }}
              rightSide={
                <Space size={8}>
                  <ColumnFilter filterConfig={incidentReportListFilter} />

                  <Button onClick={handleDownload} icon={<CloudDownloadOutlined />} disabled={!!!tableControls.getSelectedRows().length}>
                    Export
                  </Button>

                  <Button onClick={handlePrint} icon={<PrintIcon />} disabled={!!!tableControls.getSelectedRows().length}>
                    Print
                  </Button>

                  {featureStore.isIncidentReporterSendViaEmailEnabled && (
                    <Button
                      onClick={() => sendReportsByEmailModal.showModal()}
                      icon={<MailOutlined />}
                      disabled={!!!tableControls.getSelectedRows().length}>
                      Send via Email
                    </Button>
                  )}

                  <Button icon={<EditIcon />} size="middle" onClick={handleEditClick}>
                    Edit
                  </Button>

                  {showDeleteButton && (
                    <Button
                      icon={<DeleteIcon stroke="#505762" />}
                      size="middle"
                      onClick={deleteReportModal.showModal}
                      className="mobile-btn">
                      Delete
                    </Button>
                  )}

                  {showCreateButton && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      size="middle"
                      onClick={() => navigate(`${baseRoute}/incident-reports/create`)}
                      className="mobile-btn">
                      Create Report
                    </Button>
                  )}
                </Space>
              }
            />
          </Col>
        </Row>
        <div style={{ height: 'calc(100vh - 206px)', overflowY: 'auto' }}>
          <TableBase selectFirstRowByDefault selectableWithCheckboxes enableSingleRowSelectionWithCheckboxes />
        </div>
      </TableContextProvider>
      <ConfirmDeleteModal
        isEdit={false}
        initData={deleteReportModal?.initData}
        title="Delete"
        isOpen={deleteReportModal?.isOpenModal}
        onClose={deleteReportModal?.hideModal}
      />
      <SendEmailModal
        initData={sendReportsByEmailModal.initData}
        isOpen={sendReportsByEmailModal.isOpenModal}
        title="Send via Email"
        onClose={sendReportsByEmailModal.hideModal}
      />
    </>
  );
});
